import { useState, useEffect, useContext } from "react";

// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { Theme } from "@mui/material/styles";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Custom styles for the Configurator
import ConfiguratorRoot from "parts/Configurator/ConfiguratorRoot";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setMiniSidenav,
  setFixedNavbar,
  setSidenavColor,
  setDarkMode,
  setShowDeadlineBanner,
  LanguageContext,
  setDeadlineBannerManuallyHidden
} from "context";

import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { getUserSettings, updateMiniSidenav, updateFixedNavbar,
         updateDarkMode, updateSidenavColor, updateTransparentSidenav,
         updateWhiteSidenav, updateShowDeadlineBanner } from "@shared/api/userSettings";

interface ConfiguratorProps {
  isMobile: boolean;
}

function Configurator({ isMobile }: ConfiguratorProps): JSX.Element {
  const [controller, dispatch] = useMaterialUIController();
  const {
    openConfigurator,
    miniSidenav,
    fixedNavbar,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    showDeadlineBanner,
  } = controller;
  const [disabled, setDisabled] = useState<boolean>(false);
  const sidenavColors: (
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
  )[] = ["primary", "dark", "info", "success", "warning", "error"];

  const { t } = useContext(LanguageContext) as any;
  const { refresh, session } = useSessionContext();
  const { showNotification } = useNotification();

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  //handle
  const handleCloseConfigurator = async () =>{
    setOpenConfigurator(dispatch, false);
  }

  const handleSidenavColor = async (newColor: any) => {
    setSidenavColor(dispatch, newColor);
    try {
      await updateSidenavColor(session.access_token, newColor);

      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`handleSidenavColor::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  };

  const handleTransparentSidenav = async () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
    try {
      await updateTransparentSidenav(session.access_token, true);
      await updateWhiteSidenav(session.access_token, false);

      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`handleTransparentSidenav::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  };

  const handleWhiteSidenav = async () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
    try {
      await updateWhiteSidenav(session.access_token, true);
      await updateTransparentSidenav(session.access_token, false);

      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`handleWhiteSidenav::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  };

  const handleDarkSidenav = async () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
    try {
      await updateWhiteSidenav(session.access_token, false);
      await updateTransparentSidenav(session.access_token, false);

      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`handleDarkSidenav::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  };

  const handleMiniSidenav = async () => {
    setMiniSidenav(dispatch, !miniSidenav);
    try {
      await updateMiniSidenav(session.access_token, !miniSidenav);
      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`handleMiniSidenav::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  }

  const handleFixedNavbar = async () => {
    setFixedNavbar(dispatch, !fixedNavbar);
    try {
      await updateFixedNavbar(session.access_token, !fixedNavbar);
      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`handleFixedNavbar::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  }

  const handleDarkMode = async () => {
    setDarkMode(dispatch, !darkMode);
    try {
      await updateDarkMode(session.access_token, !darkMode);
      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`handleDarkMode::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  }

  const handleDispDeadLine = async () => {
    const newValue = !showDeadlineBanner;
    setShowDeadlineBanner(dispatch, newValue);

    try {
      await updateShowDeadlineBanner(session.access_token, newValue);
      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      },2000);
      console.log(`updateShowDeadlineBanner::success`);
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      },2000);
    }
  };

  const handleRedisplay = async () => {
    setDeadlineBannerManuallyHidden(dispatch, false);

    showNotification({
      icon: "check_circle",
      title: t("Common.success"),
      message: t("configurator.redisplay-deadline-banner-success"),
    }, 2000);
  };

  // 初期設定の取得
  useEffect(() => {
    const fetchSettings = async () => {
      if (!session?.access_token) {
        await refresh();
        if (!session?.access_token) {
          return;
        }
      }
      const settings = await getUserSettings(session.access_token);
      console.log(`★user-settings::${JSON.stringify(settings)}`);

      if (settings) {
        setMiniSidenav(dispatch, settings.mini_sidenav);
        setFixedNavbar(dispatch, settings.fixed_navbar);
        setSidenavColor(dispatch, settings.sidenav_color);
        setTransparentSidenav(dispatch, settings.transparent_sidenav);
        setWhiteSidenav(dispatch, settings.white_sidenav);
        setDarkMode(dispatch, settings.dark_mode);
        setShowDeadlineBanner(dispatch, settings.show_deadline_banner);
      }
    };
    fetchSettings();
  }, [session?.access_token]);

  // sidenav type buttons styles
  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }: Theme | any) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  // sidenav type active button styles
  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }: Theme | any) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">{t("configurator.configurator")}</MDTypography>
          <MDTypography variant="body2" color="text">
            {t("configurator.see")}
          </MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />

      <MDBox pt={0.5} pb={3} px={3}>
        <MDBox>
          <MDTypography variant="h6">{t("configurator.sidenav-colors")}</MDTypography>

          <MDBox mb={0.5}>
            {sidenavColors.map((color) => (
              <IconButton
                key={color}
                sx={({
                  borders: { borderWidth },
                  palette: { white, dark, background },
                  transitions,
                }: Theme | any) => ({
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                  borderColor: () => {
                    let borderColorValue = sidenavColor === color && dark.main;

                    if (darkMode && sidenavColor === color) {
                      borderColorValue = white.main;
                    }

                    return borderColorValue;
                  },
                  transition: transitions.create("border-color", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                  backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    linearGradient(gradients[color].main, gradients[color].state),

                  "&:not(:last-child)": {
                    mr: 1,
                  },

                  "&:hover, &:focus, &:active": {
                    borderColor: darkMode ? white.main : dark.main,
                  },
                })}
                onClick={() => handleSidenavColor(color)}
              />
            ))}
          </MDBox>
        </MDBox>

        <MDBox mt={3} lineHeight={1}>
          <MDTypography variant="h6">{t("configurator.sidenav-types")}</MDTypography>
          <MDTypography variant="button" color="text">
            {t("configurator.choose-sidenav-types")}
          </MDTypography>

          <MDBox
            sx={{
              display: "flex",
              mt: 2,
              mr: 1,
            }}
          >
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={(theme) =>
                !transparentSidenav && !whiteSidenav
                  ? sidenavTypeActiveButtonStyles(theme)
                  : sidenavTypeButtonsStyles(theme)
              }
            >
              Dark
            </MDButton>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={(theme) =>
                  transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles(theme)
                    : sidenavTypeButtonsStyles(theme)
                }
              >
                Transparent
              </MDButton>
            </MDBox>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled}
              fullWidth
              sx={(theme) =>
                whiteSidenav && !transparentSidenav
                  ? sidenavTypeActiveButtonStyles(theme)
                  : sidenavTypeButtonsStyles(theme)
              }
            >
              White
            </MDButton>
          </MDBox>
        </MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          lineHeight={1}
        >
          <MDTypography variant="h6">{t("configurator.sidenav-fixed")}</MDTypography>

          <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">{t("configurator.sidenav-mini")}</MDTypography>

          <Switch checked={miniSidenav} onChange={handleMiniSidenav} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">{t("configurator.light-dark")}</MDTypography>

          <Switch checked={darkMode} onChange={handleDarkMode} />
        </MDBox>
        <Divider />
        {!isMobile && (
        <>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">{t("configurator.display-deadline-banner")}</MDTypography>

          <Switch checked={showDeadlineBanner} onChange={handleDispDeadLine} />
        </MDBox>

        <MDBox mb={2}>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={handleRedisplay}
            size="medium"
            sx={(theme) =>
              !transparentSidenav && !whiteSidenav
                ? sidenavTypeActiveButtonStyles(theme)
                : sidenavTypeButtonsStyles(theme)
            }
          >
            {t("configurator.redisplay-deadline-banner")}
          </MDButton>
        </MDBox>
        </>
      )}
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
